import { UploadActionsMenuAgRendererComponent } from './upload-actions-menu-ag-renderer.component';
import { Injectable } from '@angular/core';
import { ColDef } from 'ag-grid-community';
import { chain, isEmpty } from 'lodash';
import * as moment from 'moment';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import {
    categoryColumn, downloadColumn, downloadViewColumn,
    isResponseRequiredColumn, letterNumberColumn, responseNeededByColumn,
    SiteActionsReportParams, getSiteNumColumn, sitesColumn, titleColumn,
    zcProjectNumberColumn, visitColumn, VisitReportRendererParams, trainingMaterialsProjectColumn, reportTypeColumn, visitCollectionDateColumn
} from '../../documents';
import { Category, CategoryNaturalKey } from '../../documents/models/category';
import { GeneralReport, Report, LabReport } from '../../documents/models/report';
import { SubjectTitleRenderer } from '../../renderer/subject-title-renderer';
import {
    BooleanAgFloatingFilterComponent, CovanceGridColumn,
    dateTimeComparator, dateTimeRenderer, dateRenderer,
    HiddenDocumentAgRendererComponent, BooleanCellRendererParams, yesNoRenderer, BooleanAgFloatingFilterParams
} from '../../covance-ag-grid';
import { GridColumns } from '../../shared/grid-columns';
import { UserPermissionsStorage } from '../../user-permissions/user-permissions-storage';
import { UserPermissions } from '../../user-permissions/user-permissions';
import { protocolNameColumn, protocolNumberColumn, siteSpecificColumn } from '../../shared';

@Injectable()
export class AdminDocumentColumns {
    private readonly postedDateTimeColumn: CovanceGridColumn = {
        sort: 'desc',
        headerName: 'Posted',
        field: 'postedDateTime',
        comparator: dateTimeComparator,
        filter: 'agTextColumnFilter',
        valueGetter: (params: { data: Report }) => {
            if (!params.data) {
                return null;
            }
            return dateTimeRenderer({ value: params.data.postedDateTime });
        },
        floatingFilterComponentParams: { suppressFilterButton: true }
    };

    private readonly versionDateTimeColumn: CovanceGridColumn = {
        headerName: 'Version Date',
        field: 'versionDate',
        comparator: dateTimeComparator,
        valueGetter: (params: { data: { versionDate: moment.Moment } }) => {
            if (!params.data) {
                return null;
            }
            return dateRenderer({ value: params.data.versionDate });
        },
        filter: 'agTextColumnFilter',
        floatingFilterComponentParams: { suppressFilterButton: true },
        width: 110
    };

    private readonly versionColumn: ColDef = {
        headerName: 'Version',
        field: 'version',
        suppressMenu: true,
        filter: 'agTextColumnFilter',
        floatingFilterComponentParams: { suppressFilterButton: true },
        width: 120
    };

    private readonly regionColumn: ColDef = {
        headerName: 'Region',
        field: 'region',
        filter: 'agSetColumnFilter',
        width: 75
    };

    private readonly fileNameColumn: ColDef = {
        headerName: 'File Name',
        field: 'fileName',
        filter: 'agSetColumnFilter',
        valueGetter: (params: { data: Report }) => {
            if (!params.data) {
                return null;
            }
            if (!params.data.fileLinks) {
                return null;
            }
            const fileNames: string = chain(params.data.fileLinks)
                .filter(x => !isEmpty(x.fileName))
                .map(x => x.fileName)
                .sort()
                .value()
                .join(', ');

            return fileNames;
        },
        width: 130
    };

    private readonly reportGroupColumn: ColDef = {
        headerName: 'Report Group',
        field: 'testGroup',
        filter: 'agSetColumnFilter',
        valueGetter: (params: { data: LabReport }) => {
            if (!params.data) {
                return null;
            }
            return params.data.testGroup;
        },
        width: 75
    };

    private readonly recipientTypeColumn: ColDef = {
        headerName: 'Recipient Type',
        field: 'recipientType',
        filter: 'agSetColumnFilter',
        valueGetter: (params: { data: LabReport }) => {
            if (!params.data) {
                return null;
            }
            return params.data.recipientType;
        },
        width: 75
    };

    private readonly languageColumn: ColDef = {
        headerName: 'Language',
        field: 'language',
        valueGetter: (params: { data: Report }) => params.data.fileLinks.map(fl => fl.languageCode).join(', '),
        filterValueGetter: (params: { data: Report }) => params.data.fileLinks.map(fl => fl.languageCode),
        filter: 'agSetColumnFilter'
    };

    private readonly audienceRoleColumn: ColDef = this.gridColumns
        .multiLineStringColumn({
            title: 'Roles',
            field: 'roles',
            stringsValueGetter: (entity: GeneralReport) => entity.audienceRoles,
            tooltipGetter: (role) => [{ content: role }],
            useFilter: true
        });

    private readonly hiddenColumn: ColDef = {
        headerName: 'Hidden?',
        field: 'isHidden',
        width: 20,
        getQuickFilterText: () => { return ''; },
        cellRendererFramework: HiddenDocumentAgRendererComponent,
        filter: 'agSetColumnFilter',
        filterParams: {
            cellRenderer: (params: BooleanCellRendererParams) => yesNoRenderer(params.value)
        },
        floatingFilterComponentFramework: BooleanAgFloatingFilterComponent,
        floatingFilterComponentParams: {
            booleanValueRenderer: yesNoRenderer
        } as BooleanAgFloatingFilterParams
    };

    private readonly adminSubjectColumn: ColDef = {
        headerName: 'Subject',
        field: 'subjectId',
        width: 140,
        valueGetter: (params: SiteActionsReportParams) => {
            if (!params.data) {
                return null;
            }

            if (!params.data.subjectId) {
                return '';
            }

            const subjectTitle = SubjectTitleRenderer.renderTitle({
                data: {
                    subjectNumber: params.data.subjectNumber,
                    initials: params.data.subjectInitials,
                    screeningNumber: params.data.screeningNumber,
                    zcSubjectId: params.data.zcSubjectId
                }
            });

            return subjectTitle;
        },
        floatingFilterComponentParams: { suppressFilterButton: true },
        filter: 'agTextColumnFilter'
    };

    private readonly adminAccessionNumberColumn: ColDef = {
        headerName: 'Accession',
        field: 'accessionNumber',
        width: 95,
        valueGetter: (params: VisitReportRendererParams) => {
            if (!params.data) {
                return null;
            }

            const accessionNumbers: string = chain(params.data.visits)
                .filter(x => !isEmpty(x.accessionNumber))
                .map(x => x.accessionNumber)
                .sort()
                .value()
                .join(', ');

            return accessionNumbers;
        },
        floatingFilterComponentParams: { suppressFilterButton: true },
        filter: 'agTextColumnFilter'
    };

    private readonly generalCommunicationColumns = [
        titleColumn,
        letterNumberColumn,
        this.languageColumn,
        this.postedDateTimeColumn,
        this.hiddenColumn,
        downloadColumn
    ];

    private readonly generalTrainingMaterialsColumns = [
        categoryColumn,
        titleColumn,
        this.languageColumn,
        this.postedDateTimeColumn,
        this.hiddenColumn,
        downloadColumn
    ];

    private readonly generalLabManualsColumns = [
        categoryColumn,
        titleColumn,
        this.languageColumn,
        this.postedDateTimeColumn,
        this.hiddenColumn,
        downloadColumn
    ];

    private readonly trainingMaterialsColumns = [
        categoryColumn,
        protocolNumberColumn,
        siteSpecificColumn,
        trainingMaterialsProjectColumn,
        titleColumn,
        this.languageColumn,
        this.postedDateTimeColumn,
        this.hiddenColumn,
        downloadViewColumn
    ];

    private readonly labManualsColumns = [
        categoryColumn,
        zcProjectNumberColumn,
        titleColumn,
        this.versionColumn,
        this.versionDateTimeColumn,
        this.languageColumn,
        this.postedDateTimeColumn,
        this.regionColumn,
        this.hiddenColumn,
        downloadColumn
    ];

    private readonly siteCommunicationsColumns = [
        titleColumn,
        letterNumberColumn,
        zcProjectNumberColumn,
        sitesColumn,
        this.postedDateTimeColumn,
        this.hiddenColumn,
        downloadColumn
    ];

    private readonly sponsorCommunicationsColumns = [
        categoryColumn,
        titleColumn,
        letterNumberColumn,
        this.postedDateTimeColumn,
        this.hiddenColumn,
        downloadViewColumn
    ];

    private readonly applicationTrainingColumns = [
        titleColumn,
        this.languageColumn,
        this.audienceRoleColumn,
        this.postedDateTimeColumn,
        this.hiddenColumn,
        downloadColumn
    ];

    private readonly siteActionsColumns = [
        titleColumn,
        getSiteNumColumn(),
        this.adminSubjectColumn,
        this.adminAccessionNumberColumn,
        visitColumn,
        isResponseRequiredColumn,
        responseNeededByColumn,
        this.postedDateTimeColumn,
        this.hiddenColumn,
        downloadColumn
    ];

    private readonly labReportsColumns = [
        this.fileNameColumn,
        reportTypeColumn,
        this.adminSubjectColumn,
        this.adminAccessionNumberColumn,
        visitColumn,
        visitCollectionDateColumn,
        this.reportGroupColumn,
        this.recipientTypeColumn,
        this.postedDateTimeColumn,
        this.hiddenColumn,
        downloadColumn
    ];

    private readonly columnsDictionary: { [key in CategoryNaturalKey]?: ColDef[] } = {
        'general-communications': this.generalCommunicationColumns,
        'general-manuals': this.generalLabManualsColumns,
        'general-training-materials': this.generalTrainingMaterialsColumns,
        'application-training': this.applicationTrainingColumns,
        'lab-manuals': this.labManualsColumns,
        'site-communications': this.siteCommunicationsColumns,
        'sponsor-communications': this.sponsorCommunicationsColumns,
        'training-materials': this.trainingMaterialsColumns,
        'site-actions': this.siteActionsColumns,
        'lab-reports': this.labReportsColumns
    };

    constructor(
        private gridColumns: GridColumns,
        private permissionsStorage: UserPermissionsStorage,
    ) { }

    public getColumnDefinitions(category$: Observable<Category>): Observable<ColDef[]> {
        return combineLatest(
            category$,
            this.permissionsStorage.userPermissions
        ).pipe(
            map(([category, permissions]) => {
                const columns = this.columnsDictionary[category.categoryId];
                const editMenuColumn = this.buildEditMenuColumn(category.categoryId, permissions);

                return columns
                    ? [...columns, editMenuColumn]
                    : [];
            })
        );
    }

    private buildEditMenuColumn(categoryId: CategoryNaturalKey, permissions: UserPermissions) {
        return this.gridColumns.actionsMenuColumn({
            cellRendererFramework: UploadActionsMenuAgRendererComponent,
            categoryId: categoryId,
            userPermissions: permissions
        });
    }
}

